/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { FormStore } from "@formisch/react"
import { Form, Field as FormField, getInput, reset, useForm } from "@formisch/react"
import * as v from "valibot"

import { ChangePasswordSchema } from "../../features/session/password-schema"
import { SessionViewModel } from "../../features/session/session-view-model"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { PasswordInput } from "../components/password-input"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { Field, FieldError, FieldGroup, FieldLabel } from "../primitives/field"

/**
 * Lets the signed-in user change their password.
 *
 * The Change Password button stays disabled until the form passes
 * `ChangePasswordSchema`. After a successful change, the form is cleared.
 */
export const ChangePassword = () => {
    const form = useForm({ schema: ChangePasswordSchema, validate: "blur" })

    const sessionVM = useViewModel(SessionViewModel)
    const loggerVM = useViewModel(LoggerViewModel)

    const changePassword = async (input: v.InferOutput<typeof ChangePasswordSchema>) => {
        // The route only renders with a loaded user, but the generated API type
        // still marks the ID as optional.
        const userId = sessionVM.currentUser?.id
        if (!userId) return

        const res = await sessionVM.changeUserPassword(userId, input.oldPassword, input.newPassword)
        if (res.success) {
            loggerVM.logOk("Password changed successfully.")
            reset(form)
        } else {
            loggerVM.logErrorMessage("Failed to change password.")
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <h2 className="m-0 text-xl font-bold uppercase">Change Password</h2>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Form of={form} onSubmit={changePassword}>
                    <FieldGroup className="gap-4">
                        <PasswordField
                            form={form}
                            path="oldPassword"
                            label="Old Password"
                            autoComplete="current-password"
                        />
                        <PasswordField
                            form={form}
                            path="newPassword"
                            label="New Password"
                            autoComplete="new-password"
                        />
                        <PasswordField
                            form={form}
                            path="confirmPassword"
                            label="Confirm Password"
                            autoComplete="new-password"
                        />
                        <BusyButton
                            type="submit"
                            size="action"
                            busy={form.isSubmitting}
                            disabled={!v.is(ChangePasswordSchema, getInput(form))}
                            className="self-end"
                        >
                            Change Password
                        </BusyButton>
                    </FieldGroup>
                </Form>
            </CardContent>
        </Card>
    )
}

interface PasswordFieldProps {
    /**
     * Form state owned by the form above.
     */
    form: FormStore<typeof ChangePasswordSchema>
    /**
     * Schema field this input edits; also used to derive its id.
     */
    path: keyof v.InferInput<typeof ChangePasswordSchema>
    /**
     * Label displayed above the input.
     */
    label: string
    /**
     * Tells the password manager whether to fill a saved password or offer a new one.
     */
    autoComplete: "current-password" | "new-password"
}

/**
 * Renders a labelled password input with its validation errors.
 *
 * Every error is listed, so the user sees each password rule still broken.
 */
const PasswordField = ({ form, path, label, autoComplete }: PasswordFieldProps) => {
    const id = `change-password-${path}`

    return (
        <FormField of={form} path={[path]}>
            {(field) => {
                const invalid = isFieldInvalid(field)

                return (
                    <Field data-invalid={invalid}>
                        <FieldLabel htmlFor={id}>{label}</FieldLabel>
                        <PasswordInput
                            {...field.props}
                            id={id}
                            value={field.input}
                            aria-invalid={invalid}
                            autoComplete={autoComplete}
                            className="h-10"
                        />
                        {invalid && <FieldError errors={field.errors?.map((message) => ({ message }))} />}
                    </Field>
                )
            }}
        </FormField>
    )
}
